import { BsdsInput } from '@bsds-lib-component/react';
import React, { useEffect, useId, useState } from 'react';

import { Dropdown } from '../..';
import type { ProponentBeneficiarioFormValues } from '../../../types/vida/cadastralMaintenance/beneficiarioFormTypes';
import { getInputValue } from '../../../utils/insuredFormUtils';
import { PARENTESCO_OPTIONS } from '../../../utils/parentescoUtils';
import {
  EMPTY_PROPONENT_BENEFICIARIO_FORM_VALUES,
  isProponentBeneficiarioFormValid,
  NM_BENEFICIARIO_MAX_LENGTH,
  DS_CURSO_MAX_LENGTH,
  DS_SERIE_MAX_LENGTH,
} from '../../../utils/beneficiarioFormUtils';

type ProponentBeneficiarioFormSectionProps = {
  initialValues?: ProponentBeneficiarioFormValues;
  readOnly?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  onValuesChange?: (values: ProponentBeneficiarioFormValues) => void;
};

const ProponentBeneficiarioFormSection: React.FC<ProponentBeneficiarioFormSectionProps> = ({
  initialValues,
  readOnly = false,
  onValidityChange,
  onValuesChange,
}) => {
  const parentescoDropdownId = useId();
  const [values, setValues] = useState<ProponentBeneficiarioFormValues>(
    initialValues ?? EMPTY_PROPONENT_BENEFICIARIO_FORM_VALUES,
  );

  useEffect(() => {
    setValues(initialValues ?? EMPTY_PROPONENT_BENEFICIARIO_FORM_VALUES);
  }, [initialValues]);

  useEffect(() => {
    onValidityChange?.(isProponentBeneficiarioFormValid(values));
    onValuesChange?.(values);
  }, [values, onValidityChange, onValuesChange]);

  const handleInputChange = (field: keyof ProponentBeneficiarioFormValues) => (
    event: React.FormEvent<HTMLBsdsInputElement>,
  ) => {
    if (readOnly) {
      return;
    }
    const value = getInputValue(event);
    setValues((current) => ({ ...current, [field]: value }));
  };

  return (
    <div role="region" aria-label="Dados do beneficiário do proponente">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BsdsInput
          label="Nome do Beneficiário *"
          value={values.nmBeneficiario}
          onInput={handleInputChange('nmBeneficiario')}
          type="text"
          platform="web"
          required
          disabled={readOnly}
          clearInput={false}
          maxlength={NM_BENEFICIARIO_MAX_LENGTH}
        />
        <Dropdown
          id={parentescoDropdownId}
          label="Parentesco *"
          placeholder="Selecione o parentesco"
          value={values.cdParentesco}
          onChange={(value) => {
            if (readOnly) {
              return;
            }
            setValues((current) => ({ ...current, cdParentesco: value }));
          }}
          options={PARENTESCO_OPTIONS}
          required
          disabled={readOnly}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
        <BsdsInput
          label="Percentual de Participação *"
          value={values.pcParticipacao}
          onInput={handleInputChange('pcParticipacao')}
          type="text"
          inputmode="decimal"
          platform="web"
          required
          disabled={readOnly}
          clearInput={false}
        />
        <BsdsInput
          label="Matrícula"
          value={values.cdMatricula}
          onInput={handleInputChange('cdMatricula')}
          type="text"
          platform="web"
          disabled={readOnly}
          clearInput={false}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
        <BsdsInput
          label="Curso"
          value={values.dsCurso}
          onInput={handleInputChange('dsCurso')}
          type="text"
          platform="web"
          disabled={readOnly}
          clearInput={false}
          maxlength={DS_CURSO_MAX_LENGTH}
        />
        <BsdsInput
          label="Série"
          value={values.dsSerie}
          onInput={handleInputChange('dsSerie')}
          type="text"
          platform="web"
          disabled={readOnly}
          clearInput={false}
          maxlength={DS_SERIE_MAX_LENGTH}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
        <BsdsInput
          label="Valor Mensalidade"
          value={values.vrMensalidade}
          onInput={handleInputChange('vrMensalidade')}
          type="text"
          inputmode="decimal"
          platform="web"
          disabled={readOnly}
          clearInput={false}
        />
      </div>
    </div>
  );
};

export default ProponentBeneficiarioFormSection;
